export interface EshopBar {
    key: string;
    name: string;
    count: number;
}

type ProductReviewDestinationsLike =
    | Array<{
        destinations?: Array<{
            type?: string | null;
            key?: string | null;
            name?: string | null;
        } | null> | null;
    } | null>
    | null
    | undefined;

type DeliveryCountsLike = Array<{ destination?: string | null; count?: number | null } | null> | null | undefined;

type ReviewsLike = Array<{ submittedContents?: Array<string> | null } | null> | null | undefined;

// Per-destination review counts for the distribution chart, chosen by the correct signal:
//   - integrated destinations (heureka, drmax, idealo, notino, planeo, …) -> Campaign.reviewDeliveries
//     (reviews actually shipped there, isShipped);
//   - custom destinations (type === "custom", no push/pull integration) -> reviews whose
//     submittedContents includes the destination.
// The discriminator is integration-vs-none, NOT the confirmSubmission checkbox: isShipped is never
// set for a custom source (nothing ships to it), so its only delivery signal is that a review was
// submitted there — whether that submission was gated by the "recenze vložena" checkbox
// (confirmSubmission) or by required review text (requireContent) or neither. Keying on
// confirmSubmission dropped custom destinations that require text instead of the checkbox (e.g. BENU.cz).
// The internal Testuj.to ("tt") destination is excluded (it's the funnel's own step).
export const deriveEshopBarsHybrid = (
    productReviewDestinations: ProductReviewDestinationsLike,
    deliveryCounts: DeliveryCountsLike,
    reviews: ReviewsLike
): EshopBar[] => {
    const nameByKey = new Map<string, string>();
    const customKeys = new Set<string>(); // no-integration destinations -> count via submittedContents
    (productReviewDestinations || []).forEach((entry) => {
        (entry?.destinations || []).forEach((destination) => {
            if (!destination || destination.type === "tt") {
                return;
            }
            const key = `${destination.type}/${destination.key}`;
            if (destination.name) {
                nameByKey.set(key, destination.name);
            }
            if (destination.type === "custom") {
                customKeys.add(key);
            }
        });
    });

    // Distinct reviews submitted to each destination (the delivery signal for custom sources).
    const submittedByKey = new Map<string, number>();
    (reviews || []).forEach((review) => {
        const seen = new Set<string>();
        (review?.submittedContents || []).forEach((entry) => {
            if (!entry || seen.has(entry)) {
                return;
            }
            seen.add(entry);
            submittedByKey.set(entry, (submittedByKey.get(entry) || 0) + 1);
        });
    });

    const bars = new Map<string, EshopBar>();
    const labelFor = (key: string) => nameByKey.get(key) || key.split("/")[0];

    // Integrated destinations — from the live delivered (isShipped) counts.
    (deliveryCounts || []).forEach((entry) => {
        const destination = entry?.destination;
        if (!destination || destination.startsWith("tt/") || customKeys.has(destination)) {
            return;
        }
        bars.set(destination, { key: destination, name: labelFor(destination), count: entry?.count || 0 });
    });

    // Custom (no-integration) destinations — counted from submittedContents.
    customKeys.forEach((key) => {
        bars.set(key, { key, name: labelFor(key), count: submittedByKey.get(key) || 0 });
    });

    return [...bars.values()].sort((a, b) => b.count - a.count);
};
